import { create } from "zustand"

import { GetProductTestListDocument, type GetProductTestListQuery, type ProductTest } from "@/graphql/codegen/graphql"
import { type SearchFilters, createSearchStore } from "@/stores/createSearchStore"

export type ProductTestSearchFilters = SearchFilters & {
  productId?: number
  includeHistory?: string[]
}

const deduplicateProductTests = (newData: ProductTest[], existingData: ProductTest[]): ProductTest[] => {
  const map = new Map(existingData.map((existing) => [existing.productTestId, existing]))
  for (const newItem of newData) {
    map.set(newItem.productTestId, newItem)
  }

  // Flatten history rows to parent level
  const result: ProductTest[] = []
  for (const productTest of map.values()) {
    // Add the main product test
    result.push(productTest)

    // Add history rows if they exist
    if (productTest.history?.length) {
      for (const historyItem of productTest.history) {
        result.push({
          ...historyItem,
          lotNumber: historyItem.lotNumber,
        })
      }
    }
  }

  return result
}

export const useProductTestSearchStore = create(
  createSearchStore<ProductTest, ProductTestSearchFilters, GetProductTestListQuery>({
    documentNode: GetProductTestListDocument,
    deduplicateData: deduplicateProductTests,
    getQueryInput: (filters: ProductTestSearchFilters, offset: number, limit: number) => {
      const { productId, includeHistory, ...otherFilters } = filters
      return {
        filter: {
          productId,
          includeHistory,
          ...otherFilters,
        },
        offset,
        limit,
      }
    },
    getQueryVariables: (queryInput) => ({ input: queryInput }),
    extractDataFromResponse: (data) => ({
      __typename: data.productTest.list.__typename,
      data:
        data.productTest.list.__typename === "ProductTestListSuccess"
          ? (data.productTest.list.productTests as ProductTest[])
          : [],
      error: data.productTest.list.__typename === "ProductTestListFailure" ? data.productTest.list.error : undefined,
    }),
  })
)
